import { DateTime } from "luxon";
import { CategoryWithParts, IPart, IPartImage, PartStatus } from './../../../../models/Part';
import { CameraIcon } from "@heroicons/react/24/outline";
import Modal from "../../../../layouts/Modal";
import ImageCarousel from "../../../shared/Images/ImageCarousel";
import { useState, useMemo } from "react";
import PriceFormat from "../../../../utils/priceFormat";
import { FormatType } from "../../../../models/EbayOffer";
import formatDateTime from './../../../../utils/formatDateTime';
import useVehicleCategoryPartsOptimized from "../../../../data/useVehicleCategoryPartsOptimized";
import SearchByFiltersModal, { SearchByOption } from "../../../shared/Tables/SearchByFiltersModal";
import LoadingPulse from "../../../shared/LoadingPulse";

function formatPartStatus(status: PartStatus): string {
  return PartStatus[status] ?? status.toString();
}

function daysBetween(from: string | null | undefined, to: string | null | undefined): number {
  if (!from || !to) return 0;

  const start = DateTime.fromISO(from).startOf("day");
  const end = DateTime.fromISO(to).startOf("day");
  return Math.max(0, Math.floor(end.diff(start, "days").days));
}

function PartTableRow({ part, variant, onOpenPictures, }: {
  part: IPart;
  variant: "currentStock" | "salesHistory";
  onOpenPictures: (images: IPartImage[]) => void;
}) {
  const imageUrl = part.images?.find((image) => image.isThumbnail === true)?.url;
  const vehicle = part.vehicle;

  const offer = part.offers?.[0];

  let price: number | null = part.price ?? null;

  if (offer && variant === "currentStock") {
    price = offer.format == FormatType.Auction ? offer.auctionStartPrice ?? 0 : offer.buyItNowPrice ?? 0;
  } else if (!offer && variant === "currentStock") {
    price = part.enteredPrice ?? 0;
  }

  const daysInStock = variant === "currentStock"
    ? daysBetween(part.created, DateTime.now().toISO() ?? part.created)
    : null;

  const daysSinceFirstListed = (variant === "currentStock" && (part.originalListedDate || part.listedDate || part.offers?.[0]?.publishedOn))
    ? daysBetween(part.originalListedDate ?? part.offers?.[0]?.publishedOn ?? part.listedDate, part.soldDate ?? DateTime.now().toISO())
    : null;

  const daysToSell = (variant === "salesHistory" && part.soldDate && part.offers?.[0]?.publishedOn)
    ? daysBetween(part.offers?.[0]?.publishedOn ?? "", part.soldDate)
    : null;

  return (
    <tr className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50/50">
      <td className="align-top p-2 w-[45%]">
        <div className="flex gap-2">
          <span
            onClick={() => onOpenPictures(part.images ?? [])}
            className="flex justify-center items-center w-20 h-20 rounded bg-gray-200 shrink-0 cursor-pointer overflow-hidden"
          >
            {imageUrl ? (
              <img className="w-full h-full object-contain" src={imageUrl} alt="" />
            ) : (
              <CameraIcon className="w-8 text-gray-500" />
            )}
          </span>
          <div className="flex flex-col text-sm min-w-0">
            <div><span className="text-gray-500">Year:</span> {vehicle?.dvlaYearOfManufacture}</div>
            <div><span className="text-gray-500">Make:</span> {vehicle?.combinedMake ?? "—"}</div>
            <div><span className="text-gray-500">Model:</span> {vehicle?.combinedModel ?? "—"}</div>
            <div><span className="text-gray-500">Reg:</span> {vehicle?.vrm ?? "—"}</div>
            <div><span className="text-gray-500">Engine Code:</span> {vehicle?.engineModelCode ?? "—"}</div>
            <div><span className="text-gray-500">Fuel Type:</span> {vehicle?.fuelType ?? "—"}</div>
            <div><span className="text-gray-500">Body Style:</span> {vehicle?.bodyStyle ?? "—"}</div>
            <div><span className="text-gray-500">Doors:</span> {vehicle?.numberOfDoors ?? "—"}</div>
          </div>
        </div>
      </td>
      <td className="align-top p-2 text-sm">
        {variant === "currentStock" && (
          <>
            <div><span className="text-gray-500">Price:</span> {price ? PriceFormat(price ?? 0) : "—"}</div>
            <div><span className="text-gray-500">Days in Stock:</span> {daysInStock ?? "—"}</div>
            <div><span className="text-gray-500">Days Since First Listed:</span> {daysSinceFirstListed ?? "—"}</div>
            <div><span className="text-gray-500">Part Number:</span> {part.partNumber ?? "—"}</div>
            <div><span className="text-gray-500">Tag Number:</span> {part.tagNumber ?? "—"}</div>
            <div><span className="text-gray-500">Part Status:</span> {formatPartStatus(part.partStatus)}</div>
          </>
        )}
        {variant === "salesHistory" && (
          <>
            <div><span className="text-gray-500">Sold Price:</span> {price ? PriceFormat(price ?? 0) : "—"}</div>
            <div><span className="text-gray-500">Sold Date:</span> {formatDateTime(part.soldDate ?? "")}</div>
            <div><span className="text-gray-500">Days to Sell:</span> {daysToSell ?? "—"}</div>
            <div><span className="text-gray-500">Part Number:</span> {part.partNumber ?? "—"}</div>
            <div><span className="text-gray-500">Tag Number:</span> {part.tagNumber ?? "—"}</div>
          </>
        )}
      </td>
    </tr>
  );
}

export default function LeadPartsCategoriesTableSubRow({
  categoryWithParts,
  vehicleId,
}: {
  categoryWithParts: CategoryWithParts;
  vehicleId: string;
}) {
  const [partPicturesOpen, setPartPicturesOpen] = useState<boolean>(false);
  const [partPictures, setPartPictures] = useState<IPartImage[]>([]);

  // Filter flags for the matching rules, represented as string keys
  const isMech = categoryWithParts.isMech;

  const filterOptions: SearchByOption[] = useMemo(() => {
    const base: SearchByOption[] = [];

    if (isMech) {
      return [
        ...base,
        { label: "Match engine code", value: "matchEngineCode" },
        { label: "Match transmission", value: "matchTransmission" },
      ];
    }

    return [
      ...base,
      { label: "Match make", value: "matchMake" },
      { label: "Match model", value: "matchModel" },
      { label: "Match doors", value: "matchDoors" },
      { label: "Match exact year", value: "matchExactYear" },
      { label: "Match year range", value: "matchYearRange" },
    ];
  }, [isMech]);

  const [activeFilters, setActiveFilters] = useState<string[]>(() =>
    filterOptions.map((opt) => opt.value)
  );

  const { parts: categoryPartsQuery } = useVehicleCategoryPartsOptimized(
    vehicleId,
    categoryWithParts.category.id,
    {
      includeSold: true,
      limitSold: 50,
      matchMake: activeFilters.includes("matchMake"),
      matchModel: activeFilters.includes("matchModel"),
      matchDoors: activeFilters.includes("matchDoors"),
      matchExactYear: activeFilters.includes("matchExactYear"),
      matchYearRange: activeFilters.includes("matchYearRange"),
      matchEngineCode: activeFilters.includes("matchEngineCode"),
      matchTransmission: activeFilters.includes("matchTransmission"),
    }
  );

  const { currentStock, salesHistory } = useMemo(() => {
    const parts: IPart[] = categoryPartsQuery.data?.data ?? categoryWithParts.parts ?? [];
    const currentStock = parts.filter((p: IPart) => !p.soldDate);
    const salesHistory = parts
      .filter((p: IPart) => p.soldDate)
      .sort((a: IPart, b: IPart) => {
        const aDate = a.isInternal && a.soldDate
          ? DateTime.fromISO(a.soldDate).toMillis()
          : DateTime.fromISO(a.created).toMillis();
        const bDate = b.isInternal && b.soldDate
          ? DateTime.fromISO(b.soldDate).toMillis()
          : DateTime.fromISO(b.created).toMillis();
        return bDate - aDate;
      });
    return { currentStock, salesHistory };
  }, [categoryWithParts.parts, categoryPartsQuery.data?.data]);

  const openPictures = (images: IPartImage[]) => {
    setPartPictures(images);
    setPartPicturesOpen(true);
  };
  const isLoadingParts = categoryPartsQuery.isLoading || categoryPartsQuery.isFetching;

  return (
    <>
      <Modal open={partPicturesOpen} setOpen={setPartPicturesOpen} width={"max-w-7xl"}>
        <ImageCarousel images={partPictures} part />
      </Modal>

      <div className="flex items-center justify-between px-4 pt-4">
        <div className="text-sm font-semibold text-gray-700">
          Matching rules ({isMech ? "Mech" : "Body"})
        </div>
        <SearchByFiltersModal
          options={filterOptions}
          searchBy={activeFilters}
          setSearchBy={setActiveFilters}
          applyOnClose={true}
          disabled={categoryPartsQuery.isLoading}
          modalWidth="sm:max-w-lg"
        />
      </div>

      <div key={categoryWithParts.category.id} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 pt-2">
        <div className="flex flex-col rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
          <div className="bg-blue-600 text-white font-semibold uppercase tracking-wide text-center py-2 px-3">
            Current Stock
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    Donor Vehicle
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    Stats
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingParts ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      <LoadingPulse width="w-full" />
                    </td>
                  </tr>
                ) : currentStock.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      No current stock
                    </td>
                  </tr>
                ) : (
                  currentStock.map((part: IPart) => (
                    <PartTableRow
                      key={part.id}
                      part={part}
                      variant="currentStock"
                      onOpenPictures={openPictures}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm">
          <div className="bg-green-600 text-white font-semibold uppercase tracking-wide text-center py-2 px-3">
            Sales History
          </div>
          <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    Donor Vehicle
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider p-2 border-b border-gray-200">
                    Stats
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingParts ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      <LoadingPulse width="w-full" />
                    </td>
                  </tr>
                ) : salesHistory.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="p-4 text-sm text-gray-500 text-center">
                      No sales history
                    </td>
                  </tr>
                ) : (
                  salesHistory.map((part: IPart) => (
                    <PartTableRow
                      key={part.id}
                      part={part}
                      variant="salesHistory"
                      onOpenPictures={openPictures}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}